import { useState, useEffect } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Plus, CreditCard, Edit, Trash2, Eye } from "lucide-react";
import { AxiosError } from "axios";

interface BillingPlan {
  id: string;
  name: string;
  price: number | string;
  currency: string;
  billingCycle: string;
  features?: Record<string, unknown>;
  status: string;
  tenantId?: string;
  createdAt?: string;
}

export function BillingPlansPage() {
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<BillingPlan | null>(null);
  const [viewingPlan, setViewingPlan] = useState<BillingPlan | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    currency: "NGN",
    billingCycle: "monthly" as "monthly" | "yearly",
    features: {},
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load template billing plans (unassigned plans)
      const plansResponse = await api.get("/billing/plans/templates");
      setPlans(plansResponse.data);
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlan = async () => {
    if (!formData.name || !formData.price) {
      alert("Please fill in all required fields");
      return;
    }

    try {
      if (editingPlan) {
        // Update existing plan
        await api.put(`/billing/plans/${editingPlan.id}`, {
          name: formData.name,
          price: parseFloat(formData.price),
          currency: formData.currency,
          billingCycle: formData.billingCycle,
          features: formData.features,
        });
        alert("Plan updated successfully");
      } else {
        // Create template plan (no tenant assignment)
        await api.post("/billing/plans", {
          name: formData.name,
          price: parseFloat(formData.price),
          currency: formData.currency,
          billingCycle: formData.billingCycle,
          features: formData.features,
        });
        alert("Plan created successfully");
      }

      await loadData();
      setShowCreateModal(false);
      resetForm();
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ message?: string }>;
      alert(
        axiosError.response?.data?.message ||
          `Failed to ${editingPlan ? "update" : "create"} billing plan`
      );
    }
  };

  const handleViewPlan = async (planId: string) => {
    try {
      const response = await api.get(`/billing/plans/${planId}`);
      const plan = response.data;
      setViewingPlan(plan);
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ message?: string }>;
      alert(axiosError.response?.data?.message || "Failed to load plan details");
    }
  };

  const handleDeletePlan = async (planId: string) => {
    if (!confirm("Are you sure you want to delete this billing plan?")) {
      return;
    }

    try {
      await api.delete(`/billing/plans/${planId}`);
      await loadData();
      alert("Plan deleted successfully");
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ message?: string }>;
      alert(axiosError.response?.data?.message || "Failed to delete plan");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      price: "",
      currency: "NGN",
      billingCycle: "monthly",
      features: {},
    });
    setEditingPlan(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Billing Plans"
        description="Create and manage billing plans for tenants"
        breadcrumbItems={[{ label: "Dashboard", href: "/dashboard" }, { label: "Billing Plans" }]}
        showSearch={false}
        titleSize="md"
        actions={
          <Button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Create Plan
          </Button>
        }
      />

      {/* Template Plans */}
      {plans.length > 0 ? (
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Template Plans</h2>
          <p className="text-sm text-gray-600 mb-4">
            These plans can be assigned to tenants from the Manage Tenants page
          </p>
          <div className="space-y-3">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div>
                  <p className="font-medium text-gray-900">{plan.name}</p>
                  <p className="text-sm text-gray-600">
                    {plan.currency}{" "}
                    {typeof plan.price === "number" ? plan.price.toLocaleString() : plan.price} /{" "}
                    {plan.billingCycle}
                  </p>
                  {plan.features && Object.keys(plan.features).length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Features: {Object.keys(plan.features).join(", ")}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewPlan(plan.id)}
                    title="View Details"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Handle Decimal type from Prisma (could be string or number)
                      const priceValue =
                        typeof plan.price === "string"
                          ? parseFloat(plan.price).toString()
                          : typeof plan.price === "number"
                            ? plan.price.toString()
                            : String(plan.price || "0");

                      setFormData({
                        name: plan.name,
                        price: priceValue,
                        currency: plan.currency,
                        billingCycle: plan.billingCycle as "monthly" | "yearly",
                        features: plan.features || {},
                      });
                      setEditingPlan(plan);
                      setShowCreateModal(true);
                    }}
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeletePlan(plan.id)}
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ) : (
        <Card className="p-6">
          <div className="text-center py-8">
            <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No template plans created yet</p>
            <p className="text-sm text-gray-500 mt-2">Create a plan to get started</p>
          </div>
        </Card>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md p-6 m-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {editingPlan ? "Edit Plan" : "Create Billing Plan"}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plan Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="e.g., Bronze, Silver, Gold, Custom"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                <input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="USD">USD</option>
                  <option value="NGN">NGN</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Billing Cycle
                </label>
                <select
                  value={formData.billingCycle}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      billingCycle: e.target.value as "monthly" | "yearly",
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <div className="flex gap-2 pt-4">
                <Button onClick={handleCreatePlan} className="flex-1">
                  {editingPlan ? "Update" : "Create"} Plan
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* View Plan Modal */}
      {viewingPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md p-6 m-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Plan Details</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Plan Name</label>
                <p className="text-gray-900">{viewingPlan.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Price</label>
                <p className="text-gray-900">
                  {viewingPlan.currency}{" "}
                  {typeof viewingPlan.price === "number"
                    ? viewingPlan.price.toLocaleString()
                    : typeof viewingPlan.price === "string"
                      ? parseFloat(viewingPlan.price).toLocaleString()
                      : viewingPlan.price}{" "}
                  / {viewingPlan.billingCycle}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <p className="text-gray-900 capitalize">{viewingPlan.status}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Created</label>
                <p className="text-gray-900">
                  {new Date(viewingPlan.createdAt || new Date()).toLocaleDateString()}
                </p>
              </div>
              {viewingPlan.features && Object.keys(viewingPlan.features).length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Features</label>
                  <pre className="bg-gray-50 p-3 rounded text-sm overflow-auto">
                    {JSON.stringify(viewingPlan.features, null, 2)}
                  </pre>
                </div>
              )}
            </div>
            <div className="mt-6">
              <Button variant="outline" onClick={() => setViewingPlan(null)} className="w-full">
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}
    </PageContainer>
  );
}
